import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { PaymentAmountFields } from "./PaymentAmountFields";
import { usePaymentForm } from "../hooks/usePaymentForm";
import {
  blankPaymentForm,
  paymentFormFromPayment,
  paymentPayload,
  settlementTotal,
} from "../lib/paymentForm";
import api, { formatApiError } from "../lib/api";

const EDIT_PAYMENT_FIELD_IDS = {
  interestId: "edit-payment-interest-field",
  interestTestId: "edit-payment-interest",
  penaltyId: "edit-payment-penalty-field",
  penaltyTestId: "edit-payment-penalty",
  mainDebtId: "edit-payment-main-debt-field",
  mainDebtTestId: "edit-payment-main-debt",
  referralId: "edit-payment-referral-field",
  referralTestId: "edit-payment-referral",
  generateTestId: "generate-edit-payment-button",
  noteId: "edit-payment-note-field",
  noteTestId: "edit-payment-note",
  dateId: "edit-payment-date-field",
  dateTestId: "edit-payment-date",
  requiresPaymentDate: false,
  submitLabel: "Save Correction",
  submitTestId: "save-payment-edit-button",
};

export default function EditPaymentDialogV2({ loanId, payment, onClose, onDone }) {
  const paymentForm = usePaymentForm(blankPaymentForm());
  const [saving, setSaving] = useState(false);
  const [settling, setSettling] = useState(false);
  const { form, generate, generated, replace, update } = paymentForm;

  useEffect(() => {
    replace(paymentFormFromPayment(payment));
  }, [payment, replace]);

  const save = async (event) => {
    event.preventDefault();
    if (!generated) {
      toast.error("Generate Payment before saving the correction.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/payments/${payment.id}`, paymentPayload(form));
      toast.success("Payment corrected and balances recalculated");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const settle = async () => {
    const settlementAmount = settlementTotal(form);
    if (!form.settle_remark || !form.settled_date || settlementAmount <= 0) {
      toast.error("Enter Main Debt or Interest, then select the settlement date and remark.");
      return;
    }
    setSettling(true);
    try {
      await api.post(`/loans/${loanId}/settle`, {
        settle_remark: form.settle_remark,
        settled_date: form.settled_date,
        main_debt: Number(form.main_debt || 0),
        interest_amount: Number(form.interest_amount || 0),
        penalty_amount: Number(form.penalty_amount || 0),
        referral_commission: Number(form.referral_commission || 0),
      });
      toast.success("Loan settled and recorded");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSettling(false);
    }
  };

  return (
    <Dialog open={Boolean(payment)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="record-payment-dialog" data-testid="edit-payment-dialog">
        <DialogHeader>
          <DialogTitle>Edit Payment</DialogTitle>
          <DialogDescription>
            Correct the payment breakdown while preserving the updated audit history.
          </DialogDescription>
        </DialogHeader>
        <form className="record-payment-form" onSubmit={save}>
          <PaymentAmountFields
            config={EDIT_PAYMENT_FIELD_IDS}
            form={form}
            generated={generated}
            onChange={update}
            onGenerate={generate}
            saving={saving}
          />
          <div className="settle-loan-section">
            <div className="settlement-total" data-testid="edit-settlement-total-value">
              <span>Settlement Total</span>
              <strong>RM {settlementTotal(form).toFixed(2)}</strong>
            </div>
            <div>
              <Label htmlFor="edit-settle-remark">Settle Loan Remark</Label>
              <Textarea
                id="edit-settle-remark"
                data-testid="edit-settle-loan-remark"
                value={form.settle_remark}
                onChange={(event) => update("settle_remark", event.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="edit-settle-date">Settled Date</Label>
              <Input
                id="edit-settle-date"
                data-testid="edit-settle-loan-date"
                type="date"
                value={form.settled_date}
                onChange={(event) => update("settled_date", event.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={(
                settling
                || settlementTotal(form) <= 0
                || !form.settle_remark
                || !form.settled_date
              )}
              onClick={settle}
              data-testid="edit-settle-loan-button"
            >
              {settling ? "Settling…" : "✓ Loan Settle?"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}